import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Film, HardDrive, Image as ImageIcon, Link as LinkIcon, Music, Upload } from 'lucide-react';
import { FileImage } from '../FileImage';
import { formatDuration } from '../../utils/audioReference';
import { REFERENCE_TAGS, referenceTagInfo } from '../../utils/referenceTags';
import type { ReferenceChoice, ReferenceChoiceKind } from '../../utils/workspaceEdit';
import { useVideoPosters } from './useVideoPosters';

interface ReferencePickerProps {
  /** The button the picker opens from; it is placed under it, or above when there is no room. */
  anchor: HTMLElement;
  choices: ReferenceChoice[];
  /** Only these kinds are offered. */
  kinds?: ReferenceChoiceKind[];
  /** Tabs by kind of media (the workspace's media), or by tag (the workspace's Reference nodes). */
  groupBy?: 'kind' | 'tag';
  /** One pick: the address the node holds now, marked in the list. */
  currentUrl?: string;
  /** Pick several: each click adds or removes one and the list stays open until Done. */
  multiple?: boolean;
  /** With multiple, by choice id: what is chosen now (ticked), and what is already connected (shown, not clickable). */
  selectedIds?: string[];
  lockedIds?: string[];
  userId?: string;
  ownerUserId?: string;
  workspaceName?: string;
  /** The list also holds Reference nodes from the other workspaces of this project. */
  sharedFrom?: { projectName: string; loading: boolean };
  onPick: (choice: ReferenceChoice) => void;
  /** Without it the list has no upload button. */
  onUpload?: () => void;
  onClose: () => void;
}

interface Tab {
  id: string;
  label: string;
  test: (choice: ReferenceChoice) => boolean;
}

const KIND_TABS: Tab[] = [
  { id: 'image', label: 'Images', test: choice => choice.kind === 'image' },
  { id: 'video', label: 'Videos', test: choice => choice.kind === 'video' },
  { id: 'audio', label: 'Audio', test: choice => choice.kind === 'audio' },
];
const TAG_TABS: Tab[] = REFERENCE_TAGS.map(tag => ({ id: tag.id, label: tag.plural, test: choice => choice.tag === tag.id }));
const ALL: Tab = { id: 'all', label: 'All', test: () => true };

const KIND_TITLE: Record<string, string> = { image: 'Images', video: 'Videos', audio: 'Audio' };

const WIDTH = 384;
const GAP = 6;
const MARGIN = 12;

/**
 * A dropdown of media with pictures. It lists either the workspace's media (to fill a
 * Reference node) or its Reference nodes by name and tag (to use in an Image or Video node).
 * It opens at screen size whatever the canvas zoom, and closes on a click elsewhere, Esc,
 * or when the canvas moves.
 *
 * It floats over the canvas, outside any node card, with a menu's edge and shadow
 * (`ns-menu`), but it is solid white, as a dialog is: it opens over pictures, and pictures
 * are chosen in it by their colours, so nothing may show through. What is chosen in it is
 * lavender, the app's colour for a selection, not the tone of the node it was opened from.
 */
export const ReferencePicker: React.FC<ReferencePickerProps> = ({
  anchor, choices: allChoices, kinds, groupBy = 'kind', currentUrl, multiple = false, selectedIds = [], lockedIds = [],
  userId, ownerUserId, workspaceName, sharedFrom, onPick, onUpload, onClose,
}) => {
  const choices = useMemo(() => (kinds ? allChoices.filter(choice => kinds.includes(choice.kind)) : allChoices), [allChoices, kinds]);
  // Kind tabs for the kinds on offer; tag tabs for the tags in use
  const tabs = useMemo(() => {
    const candidates = groupBy === 'tag'
      ? TAG_TABS.filter(tab => choices.some(tab.test))
      : KIND_TABS.filter(tab => !kinds || kinds.includes(tab.id as ReferenceChoiceKind));
    return [ALL, ...candidates];
  }, [groupBy, choices, kinds]);
  const panelRef = useRef<HTMLDivElement>(null);
  const [tabId, setTabId] = useState('all');
  const [place, setPlace] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  // Under the button when it fits, else above it, kept inside the window
  useLayoutEffect(() => {
    const box = anchor.getBoundingClientRect();
    const width = Math.min(WIDTH, window.innerWidth - MARGIN * 2);
    const left = Math.min(Math.max(MARGIN, box.left), window.innerWidth - width - MARGIN);
    const below = window.innerHeight - box.bottom - GAP - MARGIN;
    const above = box.top - GAP - MARGIN;
    setPlace(below >= 320 || below >= above
      ? { left, top: box.bottom + GAP, maxHeight: Math.min(460, below) }
      : { left, bottom: window.innerHeight - box.top + GAP, maxHeight: Math.min(460, above) });
  }, [anchor]);

  useEffect(() => { panelRef.current?.focus({ preventScroll: true }); }, [place !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // A click elsewhere, Esc, the canvas moving or the window changing size closes it
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const outside = (e: Event) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || anchor.contains(target)) return;
      closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // The canvas would also clear its selection on this Esc
      e.stopPropagation();
      closeRef.current();
    };
    const onResize = () => closeRef.current();
    document.addEventListener('pointerdown', outside, true);
    window.addEventListener('wheel', outside, { capture: true, passive: true });
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      window.removeEventListener('wheel', outside, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };
  }, [anchor]);

  const tab = tabs.find(candidate => candidate.id === tabId) ?? ALL;
  const shown = choices.filter(tab.test);
  const videos = useMemo(() => choices.filter(choice => choice.kind === 'video'), [choices]);
  const { posterOf, folderLocked, allowFolder } = useVideoPosters(videos, { userId, ownerUserId, workspaceName });
  const missingPosters = folderLocked && videos.some(video => !posterOf(video));

  const empty = groupBy === 'tag'
    ? (choices.length === 0
      ? (sharedFrom
        ? (sharedFrom.loading ? `Looking in the workspaces of ${sharedFrom.projectName}...` : `No references in ${sharedFrom.projectName} yet. Add a Reference node in any of its workspaces, put a picture, clip or audio file in it, and it shows up here.`)
        : 'No references yet. Add a Reference node, put a picture, clip or audio file in it, and it shows up here.')
      : 'No references with this tag.')
    : sharedFrom?.loading && choices.length === 0
      ? `Looking in the workspaces of ${sharedFrom.projectName}...`
      : (tab.id === 'all'
        ? (sharedFrom
          ? `Nothing in ${sharedFrom.projectName} yet. Generate an image or video, upload a file, or add a Reference node in any of its workspaces.`
          : 'Nothing in this workspace yet. Generate an image or video, or upload a file. In a project, the Reference nodes of its other workspaces are offered here too.')
        : `No ${tab.label.toLowerCase()} in ${sharedFrom ? sharedFrom.projectName : 'this workspace'} yet.`);
  // With one kind and no tags to sort by, a title says what the list holds
  const title = tabs.length === 1 || (groupBy === 'kind' && tabs.length === 2)
    ? groupBy === 'tag' ? (sharedFrom ? `References in ${sharedFrom.projectName}` : 'References in this workspace') : `${KIND_TITLE[tabs[tabs.length - 1].id] ?? 'Media'} in ${sharedFrom ? sharedFrom.projectName : 'this workspace'}`
    : null;

  // The kind of media an empty list is waiting for, for the mark of its empty state
  const emptyKind = tab.id === 'video' || tab.id === 'audio' ? tab.id : kinds?.length === 1 ? kinds[0] : 'image';

  if (!place) return null;
  return createPortal(
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-label={multiple ? 'Choose references' : 'Choose a reference'}
      className="ns-menu bg-white backdrop-filter-none fixed z-[130] flex flex-col p-0 rounded-2xl overflow-hidden outline-none"
      style={{ left: place.left, top: place.top, bottom: place.bottom, width: Math.min(WIDTH, window.innerWidth - MARGIN * 2), maxHeight: place.maxHeight }}
      // The list scrolls here instead of zooming the canvas; arrow keys stay out of the canvas too
      onWheel={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key.startsWith('Arrow')) e.stopPropagation(); }}
      data-reference-picker
    >
      {title ? (
        <p className="ns-t-title px-4 pt-3 pb-2 shrink-0">
          {title}<span className="ml-1.5 font-medium tabular-nums text-ink-faint">{choices.length}</span>
        </p>
      ) : (
        <div className="px-3 pt-3 pb-2 shrink-0">
          {/* The switch of the composer; with many tags it wraps, so its corners are a panel's, not a pill's */}
          <div className="ns-seg flex-wrap max-w-full rounded-2xl" role="tablist" aria-label={groupBy === 'tag' ? 'Tag' : 'Kind of media'}>
            {tabs.map(candidate => {
              const count = choices.filter(candidate.test).length;
              const active = candidate.id === tab.id;
              return (
                <button
                  key={candidate.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  disabled={candidate.id !== 'all' && count === 0}
                  onClick={() => setTabId(candidate.id)}
                  className="ns-seg-item"
                  data-filter={candidate.id}
                >
                  {candidate.label}<span className="font-medium tabular-nums text-ink-faint">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {sharedFrom && (sharedFrom.loading || choices.some(choice => choice.workspaceId)) && (
        <p className="ns-t-meta px-4 pb-2 shrink-0" data-shared-note>
          {sharedFrom.loading
            ? `Looking in the other workspaces of ${sharedFrom.projectName}...`
            : `${choices.filter(choice => choice.workspaceId).length} from other workspaces of ${sharedFrom.projectName}${multiple ? ', used from there, not copied' : ''}.`}
        </p>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-1 pb-3">
        {shown.length === 0 ? (
          <div className="ns-empty" data-picker-empty>
            <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
              {emptyKind === 'video' ? <Film /> : emptyKind === 'audio' ? <Music /> : <ImageIcon />}
            </span>
            <p className="ns-empty-text">{empty}</p>
          </div>
        ) : (
          <ul className="grid grid-cols-3 gap-2" data-reference-choices>
            {shown.map(choice => {
              const locked = multiple && lockedIds.includes(choice.id);
              const current = multiple ? selectedIds.includes(choice.id) : !!currentUrl && choice.url === currentUrl;
              const tag = referenceTagInfo(choice.tag);
              return (
                <li key={choice.id}>
                  <button
                    type="button"
                    onClick={() => onPick(choice)}
                    disabled={locked}
                    title={locked ? `${choice.label} · already connected` : `${choice.label} · ${tag?.label ?? choice.note}${choice.workspaceName ? ` · from ${choice.workspaceName}` : ''}`}
                    className={`group w-full text-left rounded-xl p-1 transition-colors disabled:opacity-60 ${current ? 'bg-lavender-soft' : 'enabled:hover:bg-cream-200'}`}
                    aria-current={!multiple && current ? true : undefined}
                    aria-pressed={multiple && !locked ? current : undefined}
                    data-choice={choice.kind}
                    data-chosen={current || undefined}
                    data-connected={locked || undefined}
                    data-from-workspace={choice.workspaceId}
                  >
                    {/* A clip is on ink, as where it plays; a picture that has not loaded is a light tile, not a black one */}
                    <span className={`relative block aspect-video rounded-lg overflow-hidden ${choice.kind === 'video' ? 'bg-ink' : 'bg-cream-200'} ${current ? 'ring-2 ring-lavender-deep' : ''}`}>
                      <ChoicePicture choice={choice} poster={choice.kind === 'video' ? posterOf(choice) : undefined} userId={userId} workspaceName={workspaceName} />
                      {choice.durationSec ? (
                        <span className="ns-badge ns-badge-ink absolute right-1 bottom-1 tabular-nums">{formatDuration(choice.durationSec)}</span>
                      ) : null}
                      {current && (
                        <span className="absolute left-1 top-1 w-5 h-5 grid place-items-center rounded-full bg-lavender-deep text-white" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                      )}
                      {locked && (
                        <span className="absolute left-1 top-1 w-5 h-5 grid place-items-center rounded-full bg-cream text-ink" aria-hidden="true"><LinkIcon size={12} strokeWidth={2.5} /></span>
                      )}
                    </span>
                    <span className="block mt-1 px-0.5 truncate text-xs font-semibold text-ink">{choice.label}</span>
                    {locked ? (
                      <span className="ns-t-meta block px-0.5 truncate">Connected</span>
                    ) : tag ? (
                      <span className="ns-chip ns-chip-sm ns-chip-tone mt-0.5" data-tone={tag.tone} data-choice-tag={tag.id}>{tag.label}</span>
                    ) : (
                      <span className="ns-t-meta block px-0.5 truncate">{choice.note}</span>
                    )}
                    {choice.workspaceName && !locked && (
                      <span className="ns-t-meta block px-0.5 truncate" data-choice-workspace>from {choice.workspaceName}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {missingPosters && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-t border-cream-300 bg-cream-50">
          <HardDrive size={14} className="text-ink-soft shrink-0" aria-hidden="true" />
          <span className="ns-t-small flex-1">Allow the local folder to see every video's picture.</span>
          <button type="button" onClick={() => void allowFolder()} className="ns-btn ns-btn-ghost ns-btn-sm ns-btn-flat shrink-0">Allow access</button>
        </div>
      )}
      {multiple ? (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2.5 border-t border-cream-300">
          <span className="ns-t-small flex-1" data-chosen-count>
            {selectedIds.length === 0 ? 'Click to add; click again to take it off.' : `${selectedIds.length} chosen`}
          </span>
          <button type="button" onClick={onClose} className="ns-btn ns-btn-primary ns-btn-sm ns-btn-flat" data-picker-done>Done</button>
        </div>
      ) : onUpload && (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2.5 border-t border-cream-300">
          <button
            type="button"
            onClick={onUpload}
            className="ns-btn ns-btn-ghost ns-btn-sm ns-btn-flat"
            data-upload-reference
          >
            <Upload size={14} /> Upload from computer
          </button>
          <span className="ns-t-meta">or drop a file on the node</span>
        </div>
      )}
    </div>,
    document.body,
  );
};

const ChoicePicture: React.FC<{ choice: ReferenceChoice; poster?: string; userId?: string; workspaceName?: string }> = ({ choice, poster, userId, workspaceName }) => {
  const icon = (Icon: typeof Film, dark: boolean) => (
    <span className={`absolute inset-0 grid place-items-center ${dark ? 'text-cream/50' : 'text-ink-soft'}`}><Icon size={16} /></span>
  );
  if (choice.kind === 'image') {
    return <FileImage src={choice.url} userId={userId} workspaceName={workspaceName} alt="" className="w-full h-full object-cover" fallback={icon(ImageIcon, false)} />;
  }
  if (choice.kind === 'video') {
    return poster ? <img src={poster} alt="" draggable={false} className="w-full h-full object-cover" data-poster /> : icon(Film, true);
  }
  return icon(Music, false);
};

export default ReferencePicker;
